<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md8 lg6>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="loading">
            <spinner />
          </div>
          <div v-else-if="loadError">
            <p>{{ loadError }}</p>
            <main-button :text="adminText('workflowsAll.gratis_retry', 'Retry')" @click.prevent="loadEvent" />
          </div>
          <form
            v-else
            v-scroll-to-error
            class="mobile-button-container"
            @submit.prevent="submitForm"
          >
            <div class="title-screen">
              <span>{{ adminText('workflowsAll.create_gratis_ticket_a3c24cf', 'Create gratis ticket') }}</span>
            </div>
            <v-layout v-if="isSuperAdmin" class="pb-16">
              <v-flex xs12>
                <select-field
                  v-model="printFormat"
                  :items="localizedPrintFormatItems"
                  item-render="caption"
                  item-select="value"
                  :label="adminText('workflowsAll.print_format_811ba19', 'Print format')"
                />
              </v-flex>
            </v-layout>
            <gratis-ticket-form
              v-if="activeStep === 1 && !createdOrderUUID && !pendingGratisRequest"
              :vs="$v"
              :currency="event.currency"
              :event="event"
              :initial-values="form"
              :ticket-types="ticketTypes"
              :is-floor-plan="event.extraModules.floorPlan"
              :max-initial-quantity="calculateMaxInitialQuantity()"
            />
            <p v-else-if="createdOrderUUID">{{ adminText('workflowsAll.gratis_created_retry', 'Tickets have been created. Retry the download below.') }}</p>
            <p v-else-if="pendingGratisRequest">
              {{ downloading ? adminText('workflowsAll.gratis_creating', 'Creating tickets…') : adminText('workflowsAll.gratis_uncertain_issuance', 'The issuance status could not be confirmed. Retry to retrieve the same tickets.') }}
            </p>
            <div v-else>
              <gratis-ticket-floor-plan
                ref="GratisTicketFloorPlan"
                :initial-venue="initialVenue"
                :init-venue-availability="venueAvailability"
                :initial-values="form"
                :order-items="floorPlanOrderItems"
                :max-quantity="calculateMaxInitialQuantity()"
              />
            </div>
            <div class="buttons-container">
              <main-button v-if="activeStep === 1 || pendingGratisRequest" :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat @click.prevent="goBack" />
              <main-button v-else-if="!createdOrderUUID" :text="adminText('workflowsAll.back_b52b36b', 'Back')" flat @click.prevent="activeStep = 1" />
              <main-button
                v-if="requiresFloorPlan && activeStep === 1 && !createdOrderUUID && !pendingGratisRequest"
                :text="adminText('workflowsAll.next_bc98198', 'Next')"
                :loading="downloading"
                @click.prevent="toFloorPlan"
              />
              <main-button
                v-else
                :text="createdOrderUUID ? adminText('workflowsAll.gratis_download_again', 'Download tickets again') : pendingGratisRequest ? adminText('workflowsAll.gratis_retry_issuance', 'Retry issuance') : adminText('workflowsAll.create_download_tickets_4ed0c49', 'Create & download tickets')"
                depressed
                :disabled="!user.isVerifiedByCoreEvent || (!createdOrderUUID && !pendingGratisRequest && isTableMode && !form.initialQuantity)"
                :loading="downloading"
              />
              <a v-show="false" id="download-ticket" />
            </div>
          </form>
        </v-flex>
        <simple-snackbar :open.sync="snackbarOpen" :message-prop="workflowLabel(snackbarMessage)" color="red" />
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import GratisTicketForm from './gratis-ticket-form'
import GratisTicketFloorPlan from './gratis-ticket-floor-plan'
import MainButton from './../shared/main-button'
import { submitGuard, frontendURL } from './../../utils'
import { createGratisTickets } from '../../http'
import Spinner from '../shared/spinner.vue'
import { required, maxLength, minValue, requiredIf, maxValue, integer } from 'vuelidate/lib/validators'
import { GET_EVENT_DETAILS_WITH_ORDER_ITEMS } from '@/graphql/event.graphql'
import axios from 'axios'
import { GET_EVENT_FLOOR_PLAN } from '@/graphql/event.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { ValidityType, TicketType } from '@/types'
import uuid from 'uuid/v4'

const MAX_GRATIS_TICKETS_PER_CREATION = 40

export default {
  mixins: [WorkflowDisplay],
  name: 'create-gratis-ticket',
  components: {
    GratisTicketForm,
    GratisTicketFloorPlan,
    MainButton,
    Spinner,
    SimpleSnackbar,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    user: Object,
    organizationSlug: {
      default: null,
      type: String,
    },
  },
  data: () => ({
    event: { extraModules: {} },
    form: {
      name: '', price: '0', ticketType: null, initialQuantity: null,
      selectedSeats: [], tableSelections: [], description: '',
      hasDateValidationRange: false, entryFrom: null, entryTo: null, validityType: null,
    },
    loading: true,
    downloading: false,
    activeStep: 1,
    printFormat: 'Web format',
    printFormatItems: ['Web format', 'Ticket seller format'],
    venueAvailability: {},
    initialVenue: {},
    snackbarOpen: false,
    snackbarMessage: '',
    floorPlanOrderItems: [],
    loadError: '',
    createdOrderUUID: null,
    pendingGratisRequest: null,
  }),
  computed: {
    localizedPrintFormatItems() { return this.printFormatItems.map(value => ({ value, caption: this.workflowPrintFormatOption(value) })) },
    isTableMode() { return this.form.ticketType === TicketType.TABLE },
    requiresFloorPlan() {
      return !!this.event.extraModules.floorPlan && this.form.ticketType !== TicketType.PARTER
    },
    ticketTypes() {
      const sources = this.floorPlanOrderItems.filter(item => !item.itemMeta.isGratis)
      const hasLocations = item => (item.sectionSeats || []).some(id => (this.initialVenue.seats || []).includes(id))
      const types = []
      if (sources.some(item => item.itemMeta.parter || (!item.itemMeta.table && !(item.sectionSeats || []).length))) types.push({ text: this.workflowTicketRowParter(), value: TicketType.PARTER })
      if (sources.some(item => !item.itemMeta.table && !item.itemMeta.parter && hasLocations(item))) types.push({ text: this.workflowTicketRowSeat(), value: TicketType.REGULAR })
      if (sources.some(item => item.itemMeta.table && hasLocations(item))) types.push({ text: this.workflowTicketRowTable(), value: TicketType.TABLE })

      return types
    },
    needsQuantity() { return !this.isTableMode || this.activeStep === 2 },
    isSuperAdmin() {
      return this.user && this.user.type === 'SUPER_ADMIN'
    },
  },
  validations() {
    return {
      form: {
        name: { required },
        price: { required },
        initialQuantity: {
          required: requiredIf(() => this.needsQuantity),
          maxValue: value => !this.needsQuantity || maxValue(this.calculateMaxInitialQuantity())(value),
          minValue: value => !this.needsQuantity || minValue(1)(value),
          integer: value => !this.needsQuantity || integer(value),
        },
        description: { maxLength: maxLength(256) },
        ticketType: { required: requiredIf(() => this.event.extraModules.floorPlan) },
        hasDateValidationRange: {},
        entryFrom: { required: requiredIf(() => !!this.form.hasDateValidationRange) },
        entryTo: {
          required: requiredIf(
            () =>
              !!this.form.hasDateValidationRange &&
              this.form.validityType === ValidityType.MULTIPLE_DAYS,
          ),
        },
      },
    }
  },
  watch: {
    'form.ticketType'() {
      this.form.selectedSeats = []
      this.form.tableSelections = []
      this.form.initialQuantity = this.isTableMode ? 0 : null
    },
  },
  mounted() {
    this.restorePendingGratisRequest()
    this.loadEvent()
  },
  methods: {
    pendingRequestStorageKey() { return `gratis-ticket-request:${this.eventId}:${this.user.id}` },
    restorePendingGratisRequest() {
      try {
        const pending = JSON.parse(window.sessionStorage.getItem(this.pendingRequestStorageKey()))
        if (pending && typeof pending.requestId === 'string' && pending.input &&
            (pending.floorPlanSelections === null || Array.isArray(pending.floorPlanSelections))) {
          this.pendingGratisRequest = pending
        }
      } catch (e) { /* Recovery still works in memory when browser storage is unavailable. */ }
    },
    savePendingGratisRequest(request) {
      this.pendingGratisRequest = request
      try { window.sessionStorage.setItem(this.pendingRequestStorageKey(), JSON.stringify(request)) } catch (e) { /* Storage can be disabled. */ }
    },
    clearPendingGratisRequest() {
      this.pendingGratisRequest = null
      try { window.sessionStorage.removeItem(this.pendingRequestStorageKey()) } catch (e) { /* Storage can be disabled. */ }
    },
    async restorePendingForm(request) {
      this.form.ticketType = request.ticketType
      await this.$nextTick()
      Object.assign(this.form, request.input, {
        selectedSeats: request.ticketType === TicketType.REGULAR ? (request.floorPlanSelections || []).map(selection => selection.locationId) : [],
        tableSelections: request.ticketType === TicketType.TABLE ? request.floorPlanSelections || [] : [],
      })
    },
    async loadEvent() {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await this.$apollo.query({
          query: GET_EVENT_DETAILS_WITH_ORDER_ITEMS,
          variables: { eventId: this.eventId },
          fetchPolicy: 'network-only',
        })
        this.event = data.getEvent
        if (this.event.extraModules.floorPlan && !this.pendingGratisRequest) {
          await this.getFloorPlan()
          if (!this.ticketTypes.length) throw new Error(this.adminText('workflowsAll.gratis_no_types', 'No configured ticket types are available on this floor plan.'))
          if (this.ticketTypes.length === 1) this.form.ticketType = this.ticketTypes[0].value
        }
      } catch (e) { this.loadError = e.message || this.adminText('workflowsAll.gratis_load_event_error', 'Unable to load this event.') }
      finally { this.loading = false }
    },
    async submitForm() {
      if (this.downloading || this.submitInProgress) return
      // Download retries reuse the committed order, even if availability has changed since issuance.
      if (this.createdOrderUUID) return this.deliverTickets()
      // Recovery uses its frozen payload, even after a reload or a change in availability.
      if (this.pendingGratisRequest) return this.issueTickets()
      if (this.requiresFloorPlan && this.activeStep === 1) return this.toFloorPlan()
      await submitGuard(this, async () => {
        if (this.requiresFloorPlan && (!this.$refs.GratisTicketFloorPlan || !this.$refs.GratisTicketFloorPlan.isSelectionValid())) {
          this.showError(this.adminText('workflowsAll.gratis_check_selection', 'Check your selected locations and ticket quantities.'))

          return
        }
        await this.issueTickets()
      })
    },
    async issueTickets() {
      if (this.downloading) return
      this.downloading = true
      try {
        await this.createTickets()
      } catch (e) {
        // A validation rejection is definitive. Network/server failures keep the same key and payload.
        const rejected = (e.graphQLErrors || []).some(error => error.extensions && error.extensions.statusCode === 400)
        if (rejected && this.pendingGratisRequest) {
          const request = this.pendingGratisRequest
          this.clearPendingGratisRequest()
          await this.restorePendingForm(request)
        }
        this.showError(e.message || this.adminText('workflowsAll.gratis_creation_error', 'Unable to create gratis tickets.'))
        if (this.requiresFloorPlan && !this.pendingGratisRequest && !this.createdOrderUUID) {
          try { await this.getFloorPlan() } catch (refreshError) { this.showError(refreshError.message) }
        }
      } finally { this.downloading = false }
    },
    showError(message) { this.snackbarMessage = message; this.snackbarOpen = true },
    async createTickets() {
      if (!this.pendingGratisRequest) {
        const { ticketType, selectedSeats, tableSelections, ...rest } = this.form
        if (!rest.hasDateValidationRange) {
          rest.entryFrom = null
          rest.entryTo = null
          rest.validityType = null
        }
        const floorPlanSelections = !this.event.extraModules.floorPlan || ticketType === TicketType.PARTER ? null : this.isTableMode
          ? tableSelections
          : selectedSeats.map(locationId => ({ locationId, quantity: 1 }))
        this.savePendingGratisRequest(JSON.parse(JSON.stringify({
          requestId: uuid(),
          ticketType,
          input: { ...rest, currency: this.event.currency },
          floorPlanSelections,
        })))
      }
      const request = this.pendingGratisRequest
      this.createdOrderUUID = await createGratisTickets(
        this.eventId,
        request.input,
        null,
        request.floorPlanSelections,
        request.requestId,
      )
      if (!this.createdOrderUUID) throw new Error(this.adminText('workflowsAll.gratis_creation_error', 'Unable to create gratis tickets.'))
      await this.deliverTickets()
    },
    async deliverTickets() {
      this.downloading = true
      try {
        if (this.isSuperAdmin && this.printFormat === 'Ticket seller format') {
          window.open(`${frontendURL}/ticket-seller/print/${this.createdOrderUUID}`, '_blank', 'noopener')
        } else await this.downloadTickets(this.createdOrderUUID)
        this.clearPendingGratisRequest()
        this.goBack()
      } catch (e) {
        this.showError(this.adminText('workflowsAll.gratis_download_error', 'Tickets were created. Download failed; use Download tickets again to retry.'))
      } finally { this.downloading = false }
    },
    async toFloorPlan() {
      await submitGuard(this, async () => {
        this.downloading = true
        try {
          await this.getFloorPlan(true)
          this.activeStep = 2
        } catch (e) { this.showError(e.message || this.adminText('workflowsAll.gratis_load_plan_error', 'Unable to load the floor plan.')) }
        finally { this.downloading = false }
      })
    },
    async getFloorPlan(requireImage = false) {
      const { data } = await this.$apollo.query({
        query: GET_EVENT_FLOOR_PLAN,
        variables: { eventId: this.eventId },
        fetchPolicy: 'network-only',
      })
      const plan = data.getEventFloorPlan
      this.initialVenue = data.getVenues.find(venue => venue.id === plan.venueId) || {}
      if (requireImage && !this.initialVenue.imageUrl) throw new Error(this.adminText('workflowsAll.gratis_no_plan', 'This event has no configured floor plan.'))
      this.floorPlanOrderItems = plan.orderItems.map(item => ({ ...item, itemMeta: item.itemMeta || {} }))
      this.venueAvailability = (plan.venueAvailability && plan.venueAvailability.availability) || {}
    },
    async downloadTickets(uuid) {
      const { data: ticket } = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/files/tickets/${uuid}`,
      )
      const byteString = window.atob(ticket.body)
      const arrayBuffer = new ArrayBuffer(byteString.length)
      const int8Array = new Uint8Array(arrayBuffer)
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([int8Array], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const downloadLink = document.getElementById('download-ticket')
      downloadLink.href = url
      downloadLink.setAttribute('download', 'CoreEvent-karte.pdf')
      downloadLink.click()
      URL.revokeObjectURL(url)
    },
    calculateMaxInitialQuantity() {
      if (!this.event.extraModules.floorPlan && this.event.audienceCapacity) {
        return Math.min(this.event.audienceCapacity, MAX_GRATIS_TICKETS_PER_CREATION)
      }

      return MAX_GRATIS_TICKETS_PER_CREATION
    },
  },
}
</script>

<style scoped>
.buttons-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}
@media screen and (max-width: 600px) {
  .buttons-container {
    flex-direction: column-reverse;
    align-items: stretch;
    margin-top: 24px;
  }
  .buttons-container .main-button { width: 100%; min-height: 48px; margin: 0 !important; }
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .mobile-button-container {
    margin-top: 1.5rem;
  }
}
</style>
